import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AutoTranslate } from "@/lib/autoTranslate.tsx";
import { useAuth } from "@/hooks/useAuth";
import { PaystackPayment } from "@/components/PaystackPayment";
import { useToast } from "@/hooks/use-toast";
import {
  buildScheduledDowngradeMessage,
  formatSubscriptionEndDate,
  hasActiveProAccess,
} from "@/lib/subscription";

interface PricingProps {
  onPaymentStart?: () => void;
}

export function Pricing({ onPaymentStart }: PricingProps = {}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [subscriptionCanceled, setSubscriptionCanceled] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isDowngrading, setIsDowngrading] = useState(false);

  const annualPricingTrans = AutoTranslate(
    `Save 25% – billed annually ($90/year)`,
  );
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly",
  );

  // Fetch user's current subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      if (user) {
        const { data, error } = await supabase
          .from("profiles")
          .select("subscription_tier, subscription_end, subscription_canceled")
          .eq("user_id", user.id)
          .single();

        if (data) {
          setSubscriptionTier(data.subscription_tier);
          setSubscriptionEnd(data.subscription_end);
          setSubscriptionCanceled(Boolean(data.subscription_canceled));
        }
      }
      setIsLoadingProfile(false);
    };

    fetchSubscription();
  }, [user]);

  // Prices in dollars ($)
  const prices = {
    pro: { monthly: 10, yearly: 90 },
  };

  type FeatureItem = string | { text: string; bold: string; suffix: string };

  const plans = {
    starter: {
      name: AutoTranslate(`Starter`),
      price: { monthly: 0, yearly: 0 },
      description: AutoTranslate(`Begin your journey with essential tools`),
      features: [
        AutoTranslate(`Smart AI-powered conversational assistance`),
        AutoTranslate(`3 'Refresh Chat' each month (3 resumes)`),
        AutoTranslate(`3-min audio recording time`),
      ],
    },
    pro: {
      name: AutoTranslate(`Pro`),
      price: { monthly: 10, yearly: 90 },
      description: AutoTranslate(`Everything you need to land your dream job`),
      features: [
        AutoTranslate(`Smart AI-powered conversational assistance`),
        AutoTranslate(`Unlimited 'Refresh Chat' = unlimited resumes`),
        AutoTranslate(`Access to Saved Resumes page`),
        AutoTranslate(`Unlimited audio recording time`),
        AutoTranslate(`Optimize your old resume`),
        {
          text: AutoTranslate(`Share your resume with other `),
          bold: AutoTranslate(`app users`),
          suffix: AutoTranslate(` and get feedback`),
        },
        AutoTranslate(`Track your shared resume analytics`),
      ],
    },
  };

  const getProPrice = () => {
    return billingCycle === "monthly" ? prices.pro.monthly : prices.pro.yearly;
  };

  const getDisplayMonthlyPrice = () => {
    return billingCycle === "monthly" ? "10" : "7.50";
  };

  const getPlanName = () => {
    return billingCycle === "monthly" ? "Pro Monthly" : "Pro Yearly";
  };

  const handlePaymentSuccess = async () => {
    if (user) {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_tier, subscription_end, subscription_canceled")
        .eq("user_id", user.id)
        .single();

      if (data) {
        setSubscriptionTier(data.subscription_tier);
        setSubscriptionEnd(data.subscription_end);
        setSubscriptionCanceled(Boolean(data.subscription_canceled));
      }
    }
  };

  const handleDowngrade = async () => {
    if (!user || isDowngrading || subscriptionCanceled) return;

    try {
      setIsDowngrading(true);
      const { data, error } = await supabase.functions.invoke("cancel-subscription");
      if (error) throw error;

      const endDate = data?.periodEnd || data?.subscription_end || subscriptionEnd;
      await handlePaymentSuccess();
      toast({
        title: "Downgrade scheduled",
        description: buildScheduledDowngradeMessage(endDate),
        duration: 20000,
      });
    } catch (error) {
      toast({
        title: "Could not downgrade",
        description: "Please try again. If this keeps happening, contact support.",
        variant: "destructive",
      });
    } finally {
      setIsDowngrading(false);
    }
  };

  const isPro = hasActiveProAccess({
    subscription_tier: subscriptionTier,
    subscription_end: subscriptionEnd,
    subscription_canceled: subscriptionCanceled,
  });

  if (isLoadingProfile) {
    return (
      <div className="py-8 text-center" role="status">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="text-center mb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
          {AutoTranslate(
            `Get started for FREE. Upgrade to get additional benefits.`,
          )}
        </h2>
        <p className="text-xl text-muted-foreground mb-8">
          {AutoTranslate(`Choose the plan that works best for you`)}
        </p>

        <Tabs
          value={billingCycle}
          onValueChange={(value) =>
            setBillingCycle(value as "monthly" | "yearly")
          }
          className="inline-block"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="monthly">
              {AutoTranslate(`Monthly`)}
            </TabsTrigger>
            <TabsTrigger value="yearly">
              {AutoTranslate(`Yearly`)}
              <Badge variant="secondary" className="ml-2">
                Save 25%
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {/* Starter Plan */}
        <Card className={`relative bg-card ${isPro ? "border-card-border" : "border-primary shadow-lg"}`}>
          {!isPro && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-primary text-primary-foreground">
                {AutoTranslate(`Current Plan`)}
              </Badge>
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-2xl">{plans.starter.name}</CardTitle>
            <CardDescription>{plans.starter.description}</CardDescription>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-extrabold text-foreground">
                {AutoTranslate(`FREE`)}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isPro ? (
              <Button
                className="w-full"
                variant="outline"
                onClick={handleDowngrade}
                disabled={isDowngrading || subscriptionCanceled}
              >
                {subscriptionCanceled
                  ? `Downgrade scheduled for ${formatSubscriptionEndDate(subscriptionEnd)}`
                  : isDowngrading
                    ? "Scheduling downgrade..."
                    : "Downgrade"}
              </Button>
            ) : (
              <Button className="w-full" variant="outline" disabled>
                {AutoTranslate(`Current Plan`)}
              </Button>
            )}
            <ul className="divide-y divide-gray-200">
              {plans.starter.features.map((feature, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-muted-foreground">
                    {typeof feature === "string" ? (
                      feature
                    ) : (
                      <>
                        {(feature as any).text}
                        <strong>{(feature as any).bold}</strong>
                        {(feature as any).suffix}
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Pro Plan */}
        <Card className={`relative bg-card ${isPro ? "border-primary shadow-lg scale-105" : "border-card-border"}`}>
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <Badge className="bg-primary text-primary-foreground">
              {isPro
                ? AutoTranslate(`Current Plan`)
                : AutoTranslate(`Most Popular`)}
            </Badge>
          </div>
          <CardHeader>
            <CardTitle className="text-2xl">{plans.pro.name}</CardTitle>
            <CardDescription>{plans.pro.description}</CardDescription>
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold text-foreground">
                  ${getDisplayMonthlyPrice()}
                </span>
                <span className="text-lg text-muted-foreground font-medium">
                  /month
                </span>
              </div>
              {billingCycle === "yearly" && (
                <p className="text-sm text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-md">
                  {annualPricingTrans}
                </p>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isPro ? (
              <Button className="w-full" variant="outline" disabled>
                {AutoTranslate(`Current Plan - Active`)}
              </Button>
            ) : user ? (
              <PaystackPayment
                email={user.email || ""}
                amount={getProPrice()}
                planName={getPlanName()}
                onSuccess={handlePaymentSuccess}
                onBeforeOpen={onPaymentStart}
                buttonText={`Upgrade to Pro`}
              />
            ) : (
              <Button
                className="w-full"
                onClick={() => (window.location.href = "/auth")}
              >
                {AutoTranslate(`Upgrade to Pro`)}
              </Button>
            )}
            <ul className="divide-y divide-gray-200">
              {plans.pro.features.map((feature, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-muted-foreground">
                    {typeof feature === "string" ? (
                      feature
                    ) : (
                      <>
                        {feature.text}
                        <strong>{feature.bold}</strong>
                        {feature.suffix}
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
